import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { saveLayoutTemplate, pubblicaLayout, analizzaConflitti } from "../services/firebase";
import Topbar from "../components/layout/Topbar";
import { Button, Spinner } from "../components/ui";

// ─── Material Symbols ─────────────────────────────────────────────────────────
// public/index.html (React) o src/index.html (Angular):
// <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" rel="stylesheet" />

const ICON_MAP = {
  ombrellone: "beach_access",
  lettino: "bedroom_child",
  gazebo: "holiday_village",
  bar: "local_bar",
  docce: "shower",
  wc: "wc",
  bagnino: "kayaking",
  piscina: "pool",
  albero: "park",
  parcheggio: "local_parking",
  percorso: "footprint",
  spiaggia: null,
  vuoto: "backspace",
};

// name: nome icona Material Symbols diretto (es. "beach_access")
// id:   chiave ICON_MAP per elementi statici (es. "ombrellone")
function ItemIcon({ id, name: nameProp, size = 20, color = "currentColor" }) {
  const name = nameProp || ICON_MAP[id];
  if (!name) return null;
  
  return (
    <span
      className="material-symbols-outlined"
      style={{
        fontSize: `${size}px`, color, lineHeight: 1, display: "block",
        fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
        userSelect: "none",
      }}
    >
      {name}
    </span>
  );
}

// ─── Palette statica (decorazioni) ───────────────────────────────────────────
// I servizi prenotabili vengono da beach.servizi (configurati dal gestore)
const PALETTE_STATICA = [
  {
    section: "Strutture",
    items: [
      { id: "bar", label: "Bar", colore: "#854F0B", prenotabile: false },
      { id: "docce", label: "Docce", colore: "#3a8fa3", prenotabile: false },
      { id: "wc", label: "WC", colore: "#5a5a6a", prenotabile: false },
      { id: "bagnino", label: "Bagnino", colore: "#c0392b", prenotabile: false },
    ],
  },
  {
    section: "Decorazioni",
    items: [
      { id: "piscina", label: "Piscina", colore: "#0ea5e9", prenotabile: false },
      { id: "albero", label: "Albero", colore: "#16a34a", prenotabile: false },
      { id: "parcheggio", label: "Parcheggio", colore: "#6b7280", prenotabile: false },
      { id: "spiaggia", label: "Spiaggia libera", colore: "#F5C842", prenotabile: false },
      { id: "percorso", label: "Percorso", colore: "#d4c5a9", prenotabile: false },
      { id: "vuoto", label: "Cancella", colore: "#374151", prenotabile: false },
    ],
  },
];

const ALL_ITEMS_STATICI = PALETTE_STATICA.flatMap(s => s.items);
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// allItems viene popolato dinamicamente nel componente
// ma getItem è usata anche in BeachPreview, quindi usiamo una cache globale
let _allItemsCache = [...ALL_ITEMS_STATICI];
function setAllItemsCache(items) { _allItemsCache = items; }
function getItem(id) { return _allItemsCache.find(i => i.id === id); }
function emptyCell(col, row) { return { col, row, type: "vuoto" }; }

function buildGrid(cols, rows, template = []) {
  const map = {};
  template.forEach(c => { map[`${c.col}_${c.row}`] = c; });
  const cells = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      cells.push(map[`${c}_${r}`] || emptyCell(c, r));
  return cells;
}

// ─── BeachPreview ─────────────────────────────────────────────────────────────
function BeachPreview({ celle, cols, fullscreen = false }) {
  const PREVIEW_COLS = fullscreen ? cols : Math.min(cols, 8);
  const cellSize = fullscreen
    ? Math.max(36, Math.min(52, Math.floor(680 / cols)))
    : 36;
  const hasMore = !fullscreen && cols > 8;

  // Filtra le celle alle prime PREVIEW_COLS colonne
  const visibleCelle = celle.filter(c => c.col < PREVIEW_COLS);

  const renderGrid = (cellsToRender, numCols, cs) => (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${numCols}, ${cs}px)`,
      gap: "3px",
    }}>
      {cellsToRender.map((cell, i) => {
        const item = getItem(cell.type);
        const isEmpty = !item || cell.type === "vuoto";
        const isPath = cell.type === "percorso";
        const isSpiaggia = cell.type === "spiaggia";
        const isTree = cell.type === "albero";
        return (
          <div key={i} style={{
            width: `${cs}px`, height: `${cs}px`,
            borderRadius: isPath ? "3px" : isTree ? "50%" : "7px",
            background: isEmpty ? "transparent"
              : isPath ? "rgba(180,155,100,0.35)"
                : isSpiaggia ? "rgba(245,200,66,0.28)"
                  : isTree ? "rgba(56,142,60,0.25)"
                    : item.prenotabile
                      ? cell.status === "occupied" ? "rgba(183,28,28,0.2)" : "rgba(255,255,255,0.84)"
                      : `${item.colore}28`,
            border: isEmpty ? "none"
              : isPath ? "1px dashed rgba(150,120,70,0.4)"
                : isSpiaggia ? "1px solid rgba(245,200,66,0.45)"
                  : isTree ? "1px solid rgba(56,142,60,0.4)"
                    : item.prenotabile
                      ? cell.status === "occupied" ? "1px solid rgba(183,28,28,0.45)" : "1px solid rgba(255,255,255,0.92)"
                      : `1px solid ${item.colore}45`,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            position: "relative",
            boxShadow: item?.prenotabile && cell.status !== "occupied"
              ? "0 1px 3px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.8)" : "none",
          }}>
            {!isEmpty && !isSpiaggia && (
              <>
                {(item.iconaId || ICON_MAP[item.id])
                  ? <ItemIcon name={item.iconaId} id={item.id}
                    size={cs > 32 ? 18 : 13}
                    color={item.prenotabile
                      ? cell.status === "occupied" ? "rgba(183,28,28,0.8)" : "#e65100"
                      : item.colore}
                  />
                  : <div style={{ width: cs > 32 ? 12 : 9, height: cs > 32 ? 12 : 9, borderRadius: "50%", background: item.prenotabile ? "#e65100" : item.colore }} />
                }
                {cs >= 36 && item.prenotabile && (
                  <span style={{
                    fontSize: "8px", marginTop: "1px", fontWeight: 600, lineHeight: 1,
                    color: cell.status === "occupied" ? "rgba(183,28,28,0.7)" : "rgba(0,0,0,0.35)"
                  }}>
                    {LETTERS[cell.row]}{cell.col + 1}
                  </span>
                )}
                {item.prenotabile && cell.status !== "occupied" && (
                  <div style={{
                    position: "absolute", top: "2px", right: "2px",
                    width: "5px", height: "5px", borderRadius: "50%", background: "#F5C842"
                  }} />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  const sandContainer = (children) => (
    <div style={{ background: "#e8d5a3", borderRadius: "12px", overflow: "hidden", position: "relative" }}>
      {/* Texture sabbia */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "radial-gradient(circle, rgba(160,130,80,0.12) 1px, transparent 1px)",
        backgroundSize: "8px 8px", zIndex: 0,
      }} />
      {/* Mare */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{
          height: "44px",
          background: "linear-gradient(180deg, #1a7fa8 0%, #3ab5c3 50%, #5dccd6 80%, #a8e4e8 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.65)", letterSpacing: "0.15em" }}>〰 MARE 〰</span>
        </div>
        <svg viewBox={`0 0 800 16`} style={{ display: "block", marginTop: "-1px" }}
          preserveAspectRatio="none" height="14" width="100%">
          <path d="M0 8 Q200 0 400 8 Q600 16 800 8 L800 16 L0 16Z" fill="#e8d5a3" />
        </svg>
      </div>
      {/* Griglia */}
      <div style={{ padding: "10px 12px 12px", position: "relative", zIndex: 1, overflowX: fullscreen ? "auto" : "hidden" }}>
        {children}
        <div style={{ height: "12px", marginTop: "6px", background: "rgba(160,130,80,0.08)", borderRadius: "0 0 6px 6px" }} />
        {/* Legenda */}
        <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
          {[
            { bg: "rgba(255,255,255,0.84)", border: "rgba(255,255,255,0.92)", label: "Libero" },
            { bg: "rgba(183,28,28,0.2)", border: "rgba(183,28,28,0.45)", label: "Occupato" },
          ].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "9.5px", color: "rgba(80,55,20,0.7)" }}>
              <div style={{ width: "9px", height: "9px", borderRadius: "3px", background: l.bg, border: `1px solid ${l.border}` }} />
              {l.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return sandContainer(
    <div style={{ position: "relative" }}>
      {renderGrid(visibleCelle, PREVIEW_COLS, cellSize)}
      {/* Sfumatura + indicatore "più colonne" */}
      {hasMore && (
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: "20px",
          width: "60px", pointerEvents: "none",
          background: "linear-gradient(to right, transparent, #e8d5a3)",
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          paddingRight: "6px",
        }}>
          <div style={{
            background: "rgba(0,0,0,0.18)", borderRadius: "20px",
            padding: "3px 8px", fontSize: "10px", color: "rgba(80,55,20,0.8)",
            fontWeight: 500,
          }}>
            +{cols - 8}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Componente principale ───────────────────────────────────────────────────
// ─── Componente principale ───────────────────────────────────────────────────
export default function BeachBuilder() {
  const { beach, admin, refreshBeach } = useAuth();
  const navigate = useNavigate();

  // ─── Palette dinamica ───────────────────────────────────────────────────────
  // I servizi prenotabili vengono da beach.servizi configurati in Servizi.jsx
  const serviziPrenotabili = (beach?.servizi || [])
    .filter(s => s.prenotabile !== false)
    .map(s => ({
      id: s.id,
      label: s.label,
      colore: s.colore || "#1f93e0",
      prenotabile: true,
      iconaId: s.iconaId || null,  // nome icona Material Symbols scelto dal gestore
    }));

  // Merge prenotabili + statici per getItem e counts
  const allItems = [
    ...serviziPrenotabili,
    ...ALL_ITEMS_STATICI,
  ];

  // Aggiorna la cache globale usata da getItem e BeachPreview
  setAllItemsCache(allItems);

  // Palette completa da renderizzare
  const palette = [
    ...(serviziPrenotabili.length > 0 ? [{
      section: "Prenotabili",
      items: serviziPrenotabili,
    }] : []),
    ...PALETTE_STATICA,
  ];

  const savedCols = beach?.gridCols || 10;
  const savedRows = beach?.gridRows || 8;

  const [gridCols, setGridCols] = useState(savedCols);
  const [gridRows, setGridRows] = useState(savedRows);
  const [colInput, setColInput] = useState(savedCols);
  const [rowInput, setRowInput] = useState(savedRows);

  const [celle, setCelle] = useState(() =>
    buildGrid(savedCols, savedRows, beach?.layoutTemplate)
  );
  console.log("🚀 ~ BeachBuilder ~ celle:", celle)

  const [selected, setSelected] = useState(() => beach?.servizi?.[0]?.id || "vuoto");
  const [activeTab, setActiveTab] = useState("builder");
  const [saving, setSaving] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [publishDal, setPublishDal] = useState("");
  const [publishAl, setPublishAl] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [conflitti, setConflitti] = useState([]);
  const [analizzando, setAnalizzando] = useState(false);
  const [conflittiConfermati, setConflittiConfermati] = useState(false);

  const isPainting = useRef(false);

  // ESC per chiudere fullscreen
  React.useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setPreviewFullscreen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);

  // ─── Paint ──────────────────────────────────────────────────────────────────
  const paintCell = useCallback((col, row) => {
    setCelle(prev => prev.map(c => {
      if (c.col !== col || c.row !== row) return c;
      if (selected === "vuoto") return emptyCell(col, row);
      return { col, row, type: selected };
    }));
  }, [selected]);

  const handleMouseDown = (col, row) => { isPainting.current = true; paintCell(col, row); };
  const handleMouseEnter = (col, row) => { if (isPainting.current) paintCell(col, row); };
  const handleMouseUp = () => { isPainting.current = false; };

  // ─── Fill row / col ─────────────────────────────────────────────────────────
  const fillRow = (rowIdx) => {
    setCelle(prev => prev.map(c => {
      if (c.row !== rowIdx) return c;
      if (selected === "vuoto") return emptyCell(c.col, rowIdx);
      return { col: c.col, row: rowIdx, type: selected };
    }));
  };

  const fillCol = (colIdx) => {
    setCelle(prev => prev.map(c => {
      if (c.col !== colIdx) return c;
      if (selected === "vuoto") return emptyCell(colIdx, c.row);
      return { col: colIdx, row: c.row, type: selected };
    }));
  };

  // ─── Resize ─────────────────────────────────────────────────────────────────
  const applyResize = (newCols, newRows) => {
    const cols = Math.max(3, Math.min(25, newCols));
    const rows = Math.max(1, Math.min(20, newRows));
    setGridCols(cols); setGridRows(rows);
    setColInput(cols); setRowInput(rows);
    setCelle(prev => buildGrid(cols, rows, prev));
  };

  const clearAll = () => setCelle(buildGrid(gridCols, gridRows));

  // ─── Save / Publish ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveLayoutTemplate(admin.beachCode, { gridCols, gridRows, celle });
      await refreshBeach();
    } finally { setSaving(false); }
  };

  // Scarica il layout come JSON — utile per debug e condivisione.
  const handleDownloadLayout = () => {
    const payload = { beachCode: admin?.beachCode, gridCols, gridRows, celle };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `layout-${beach?.nome?.replace(/\s+/g, "-").toLowerCase() || "beach"}-${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAnalizzaEPubblica = async () => {
    if (!publishDal || !publishAl) return;
    setAnalizzando(true);
    try {
      const trovati = await analizzaConflitti(admin.beachCode, { celle, dal: publishDal, al: publishAl });
      setConflitti(trovati);
      if (trovati.length === 0) {
        // Nessun conflitto — pubblica direttamente
        await handlePublish();
      }
      // Se ci sono conflitti, il modal mostra l'avviso e aspetta conferma
    } finally { setAnalizzando(false); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await saveLayoutTemplate(admin.beachCode, { gridCols, gridRows, celle });
      await pubblicaLayout(admin.beachCode, { celle, dal: publishDal, al: publishAl });
      await refreshBeach();
      setShowPublish(false);
      setConflitti([]);
      setConflittiConfermati(false);
      navigate("/beach");
    } finally { setPublishing(false); }
  };

  // ─── Stats ───────────────────────────────────────────────────────────────────
  const counts = allItems.reduce((acc, item) => {
    acc[item.id] = celle.filter(c => c.type === item.id).length;
    return acc;
  }, {});

  const CELL_SIZE = Math.max(32, Math.min(52, Math.floor(720 / gridCols)));

  const inputStyle = {
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "6px", color: "#fff", textAlign: "center",
    padding: "5px 6px", fontSize: "13px", fontFamily: "var(--font-sans)", width: "56px",
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0f1923", color: "#fff" }}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <Topbar
        title="Beach Builder"
        subtitle={`${beach?.nome || "Spiaggia"} · ${gridCols}×${gridRows}`}
        actions={
          <div style={{ display: "flex", gap: "8px" }}>
            <Button variant="ghost" onClick={clearAll}>Pulisci tutto</Button>
            <Button variant="ghost" onClick={handleDownloadLayout} title="Scarica il layout corrente come file JSON">
              ↓ JSON
            </Button>
            <Button variant="ghost" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size={14} /> : "Salva bozza"}
            </Button>
            <Button variant="sand" onClick={() => setShowPublish(true)}>Pubblica →</Button>
          </div>
        }
      />

      {/* ── Tab bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)",
        background: "#1a2836", flexShrink: 0,
      }}>
        {["builder", "preview"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: "6px 16px", borderRadius: "8px", fontSize: "12.5px",
            border: `1px solid ${activeTab === tab ? "#1f93e0" : "rgba(255,255,255,0.12)"}`,
            background: activeTab === tab ? "rgba(31,147,224,0.15)" : "transparent",
            color: activeTab === tab ? "#1f93e0" : "rgba(255,255,255,0.5)",
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {tab === "builder" ? "🛠 Editor" : "👁 Anteprima app"}
          </button>
        ))}
        <div style={{ marginLeft: "auto", fontSize: "11px", color: "rgba(255,255,255,0.25)" }}>
          💡 Drag · Clicca <strong style={{ color: "rgba(255,255,255,0.45)" }}>A·B·C</strong> per la riga ·
          Clicca <strong style={{ color: "rgba(255,255,255,0.45)" }}>1·2·3</strong> per la colonna
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── Sidebar ── */}
        <div style={{
          width: "200px", flexShrink: 0,
          background: "#1a2836", borderRight: "1px solid rgba(255,255,255,0.07)",
          display: "flex", flexDirection: "column", overflowY: "auto",
        }}>
          <div style={{ padding: "14px 12px", flex: 1 }}>

            {/* Dimensioni griglia */}
            <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "8px" }}>
              Dimensioni griglia
            </div>
            {[["Col.", colInput, setColInput, (v) => applyResize(v, gridRows)],
            ["Righe", rowInput, setRowInput, (v) => applyResize(gridCols, v)]].map(([label, val, setter, apply]) => (
              <div key={label} style={{ display: "flex", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", width: "38px" }}>{label}</span>
                <input
                  type="number" min="1" max="25" value={val}
                  onChange={e => setter(e.target.value)}
                  onBlur={e => apply(Number(e.target.value))}
                  onKeyDown={e => e.key === "Enter" && apply(Number(e.target.value))}
                  style={inputStyle}
                />
              </div>
            ))}

            <div style={{ height: "1px", background: "rgba(255,255,255,0.07)", margin: "12px 0" }} />

            {/* Avviso se nessun servizio configurato */}
            {serviziPrenotabili.length === 0 && (
              <div style={{
                background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.2)",
                borderRadius: "8px", padding: "10px", marginBottom: "12px",
                fontSize: "11px", color: "rgba(245,200,66,0.7)", lineHeight: 1.5,
              }}>
                ⚠ Nessun servizio configurato.<br />
                <span
                  onClick={() => navigate("/servizi")}
                  style={{ color: "#1f93e0", cursor: "pointer", textDecoration: "underline" }}
                >
                  Configura i servizi →
                </span>
              </div>
            )}

            {/* Palette */}
            {palette.map(section => (
              <div key={section.section} style={{ marginBottom: "14px" }}>
                <div style={{
                  fontSize: "10px", color: "rgba(255,255,255,0.35)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  marginBottom: "6px", padding: "0 4px",
                }}>
                  {section.section}
                </div>
                {section.items.map(item => (
                  <div key={item.id} onClick={() => setSelected(item.id)} style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "7px 8px", borderRadius: "8px", cursor: "pointer",
                    background: selected === item.id ? `${item.colore}25` : "transparent",
                    border: `1px solid ${selected === item.id ? item.colore + "60" : "transparent"}`,
                    marginBottom: "2px", transition: "all 0.12s",
                  }}>
                    <div style={{
                      width: "28px", height: "28px", borderRadius: "6px",
                      background: item.id === "spiaggia" ? "rgba(245,200,66,0.35)" : `${item.colore}20`,
                      border: `1px solid ${item.id === "spiaggia" ? "rgba(245,200,66,0.5)" : item.colore + "40"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {item.id !== "spiaggia" && (
                        (item.iconaId || ICON_MAP[item.id])
                          ? <ItemIcon name={item.iconaId} id={item.id} size={15} color={item.colore} />
                          : <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: item.colore }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: "12px",
                        color: selected === item.id ? "#fff" : "rgba(255,255,255,0.65)",
                        fontWeight: selected === item.id ? 500 : 400,
                      }}>
                        {item.label}
                      </div>
                      {item.prenotabile && (
                        <div style={{ fontSize: "9px", color: "#1f93e0", marginTop: "1px" }}>prenotabile</div>
                      )}
                    </div>
                    {counts[item.id] > 0 && (
                      <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", fontWeight: 500, flexShrink: 0 }}>
                        {counts[item.id]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── Area principale ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", userSelect: "none" }}>

          {activeTab === "builder" ? (
            <>
              {/* Mare */}
              <div style={{
                height: "36px", marginBottom: "4px",
                background: "linear-gradient(180deg, rgba(30,144,255,0.2) 0%, rgba(0,188,212,0.1) 100%)",
                borderRadius: "10px 10px 0 0",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", color: "rgba(30,144,255,0.5)", letterSpacing: "0.08em",
              }}>
                〰 Mare 〰
              </div>

              {/* Header colonne — click per riempire colonna */}
              <div style={{
                display: "grid",
                gridTemplateColumns: `28px repeat(${gridCols}, ${CELL_SIZE}px)`,
                gap: "2px", marginBottom: "2px",
              }}>
                <div />
                {Array.from({ length: gridCols }).map((_, ci) => (
                  <div
                    key={ci}
                    onClick={() => fillCol(ci)}
                    title={`Riempi colonna ${ci + 1}`}
                    style={{
                      height: "20px", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "9px", color: "rgba(255,255,255,0.3)", cursor: "pointer", borderRadius: "4px",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    {ci + 1}
                  </div>
                ))}
              </div>

              {/* Griglia */}
              <div style={{
                display: "grid",
                gridTemplateColumns: `28px repeat(${gridCols}, ${CELL_SIZE}px)`,
                gridTemplateRows: `repeat(${gridRows}, ${CELL_SIZE}px)`,
                gap: "2px",
              }}>
                {Array.from({ length: gridRows }).map((_, rowIdx) => (
                  <React.Fragment key={rowIdx}>
                    {/* Label riga — click per riempire riga */}
                    <div
                      onClick={() => fillRow(rowIdx)}
                      title={`Riempi riga ${LETTERS[rowIdx]}`}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "10px", color: "rgba(255,255,255,0.3)",
                        background: "rgba(255,255,255,0.03)", borderRadius: "4px",
                        cursor: "pointer", transition: "background 0.1s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                    >
                      {LETTERS[rowIdx]}
                    </div>

                    {/* Celle */}
                    {Array.from({ length: gridCols }).map((_, colIdx) => {
                      const cell = celle.find(c => c.col === colIdx && c.row === rowIdx);
                      const item = cell?.type && cell.type !== "vuoto" ? getItem(cell.type) : null;
                      const isPath = cell?.type === "percorso";
                      const isSpiaggia = cell?.type === "spiaggia";

                      return (
                        <div
                          key={`${colIdx}_${rowIdx}`}
                          onMouseDown={() => handleMouseDown(colIdx, rowIdx)}
                          onMouseEnter={e => {
                            handleMouseEnter(colIdx, rowIdx);
                            if (!item) {
                              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                            }
                          }}
                          onMouseLeave={e => {
                            if (!item) {
                              e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                              e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                            }
                          }}
                          style={{
                            width: `${CELL_SIZE}px`, height: `${CELL_SIZE}px`,
                            borderRadius: isPath ? "3px" : "7px",
                            background: !item ? "rgba(255,255,255,0.03)"
                              : isPath ? "rgba(212,197,169,0.15)"
                                : isSpiaggia ? "rgba(245,200,66,0.18)"
                                  : `${item.colore}22`,
                            border: `1px solid ${!item
                              ? "rgba(255,255,255,0.07)"
                              : isPath ? "rgba(212,197,169,0.3)"
                                : isSpiaggia ? "rgba(245,200,66,0.35)"
                                  : item.colore + "55"
                              }`,
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center",
                            cursor: "crosshair", transition: "border-color 0.08s, background 0.08s",
                            position: "relative",
                          }}
                        >
                          {item && !isSpiaggia && (
                            <>
                              {(item.iconaId || ICON_MAP[item.id])
                                ? <ItemIcon name={item.iconaId} id={item.id} size={CELL_SIZE > 40 ? 18 : 13} color={item.colore} />
                                : <div style={{ width: CELL_SIZE > 40 ? 14 : 10, height: CELL_SIZE > 40 ? 14 : 10, borderRadius: "50%", background: item.colore }} />
                              }
                              {CELL_SIZE > 36 && item.prenotabile && (
                                <span style={{ fontSize: "8px", color: item.colore, marginTop: "2px", fontWeight: 500, opacity: 0.8 }}>
                                  {LETTERS[rowIdx]}{colIdx + 1}
                                </span>
                              )}
                              {item.prenotabile && (
                                <div style={{
                                  position: "absolute", top: "2px", right: "2px",
                                  width: "5px", height: "5px", borderRadius: "50%",
                                  background: "#F5C842",
                                }} />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>

              {/* Sabbia */}
              <div style={{
                height: "24px", marginTop: "4px",
                background: "linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)",
                borderRadius: "0 0 10px 10px",
              }} />

              {/* Legenda fila */}
              <div style={{ marginTop: "16px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#F5C842" }} />
                  Riga A = prima fila (tariffa più alta)
                </div>
                {/* Riepilogo contatori */}
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {allItems.filter(i => counts[i.id] > 0 && i.id !== "vuoto").map(item => (
                    <div key={item.id} style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "8px", padding: "4px 10px",
                      fontSize: "12px", color: "rgba(255,255,255,0.6)",
                    }}>
                      <ItemIcon name={item.iconaId} id={item.id} size={12} color={item.colore} />
                      <span style={{ color: item.prenotabile ? "#F5C842" : "rgba(255,255,255,0.4)", fontWeight: 500 }}>
                        {counts[item.id]}
                      </span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* ── Anteprima ── */
            <div style={{ maxWidth: "420px", margin: "0 auto" }}>
              <div style={{
                fontSize: "11px", color: "rgba(255,255,255,0.3)",
                textAlign: "center", marginBottom: "12px",
                letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                Anteprima app mobile
              </div>
              <div style={{
                background: "#1a2836", borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.1)", padding: "16px",
              }}>
                {/* Header app */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 600 }}>🏖 {beach?.nome || "Lido"}</div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>{beach?.indirizzo?.split(",")[0] || "Salento"}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ background: "#1f93e0", color: "#fff", padding: "6px 12px", borderRadius: "20px", fontSize: "12px" }}>
                      {new Date().toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                    </div>
                    {/* Pulsante espandi */}
                    <button
                      onClick={() => setPreviewFullscreen(true)}
                      title="Espandi mappa"
                      style={{
                        width: "32px", height: "32px", borderRadius: "8px",
                        background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <span className="material-symbols-outlined" style={{
                        fontSize: "16px", color: "rgba(255,255,255,0.6)",
                        fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
                        lineHeight: 1, display: "block",
                      }}>fullscreen</span>
                    </button>
                  </div>
                </div>

                <BeachPreview celle={celle} cols={gridCols} />

                <button style={{
                  width: "100%", marginTop: "14px", padding: "12px",
                  background: "#1f93e0", border: "none", borderRadius: "10px",
                  color: "#fff", fontSize: "14px", fontWeight: 500,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                  Seleziona posto →
                </button>
              </div>

              {gridCols > 8 && (
                <div style={{ textAlign: "center", marginTop: "10px", fontSize: "11px", color: "rgba(255,255,255,0.25)" }}>
                  La mappa ha {gridCols} colonne · clicca <span
                    onClick={() => setPreviewFullscreen(true)}
                    style={{ color: "var(--ocean)", cursor: "pointer" }}
                  >espandi</span> per vederla completa
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal fullscreen mappa ── */}
      {previewFullscreen && (
        <div
          onClick={() => setPreviewFullscreen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            zIndex: 200, padding: "24px",
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "900px" }}>
            {/* Header modal */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 500 }}>🏖 {beach?.nome || "Lido"}</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>
                  Mappa completa · {gridCols} colonne × {gridRows} righe
                </div>
              </div>
              <button
                onClick={() => setPreviewFullscreen(false)}
                style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <span className="material-symbols-outlined" style={{
                  fontSize: "18px", color: "#fff",
                  fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
                  lineHeight: 1, display: "block",
                }}>close</span>
              </button>
            </div>
            {/* Mappa completa */}
            <div style={{ overflowX: "auto", borderRadius: "16px" }}>
              <BeachPreview celle={celle} cols={gridCols} fullscreen={true} />
            </div>
            <div style={{ textAlign: "center", marginTop: "12px", fontSize: "11px", color: "rgba(255,255,255,0.25)" }}>
              Premi ESC o clicca fuori per chiudere
            </div>
          </div>
        </div>
      )}

      {/* ── Modal pubblica ── */}
      {showPublish && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div style={{
            background: "#1a2836", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "16px", padding: "28px", width: "420px",
          }}>
            <div style={{ fontSize: "16px", fontWeight: 500, marginBottom: "6px" }}>Pubblica il layout</div>
            <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", marginBottom: "20px" }}>
              Seleziona il periodo stagionale. Ogni giorno del range avrà la stessa configurazione di posti.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
              {[["Dal", publishDal, setPublishDal], ["Al", publishAl, setPublishAl]].map(([label, val, setter]) => (
                <div key={label}>
                  <label style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", display: "block", marginBottom: "5px" }}>{label}</label>
                  <input type="date" value={val} onChange={e => setter(e.target.value)}
                    style={{
                      width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: "8px", color: "#fff", padding: "8px 12px",
                      fontSize: "13px", fontFamily: "inherit",
                    }} />
                </div>
              ))}
            </div>
            {publishDal && publishAl && new Date(publishAl) >= new Date(publishDal) && (
              <div style={{
                background: "rgba(31,147,224,0.1)", border: "1px solid rgba(31,147,224,0.2)",
                borderRadius: "8px", padding: "10px 14px",
                fontSize: "12.5px", color: "#1f93e0", marginBottom: "16px",
              }}>
                Verranno create {Math.ceil((new Date(publishAl) - new Date(publishDal)) / 86400000) + 1} date di disponibilità
              </div>
            )}
            {/* Avviso conflitti */}
            {conflitti.length > 0 && (
              <div style={{
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: "8px", padding: "12px 14px", marginBottom: "16px",
              }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#ef4444", marginBottom: "8px" }}>
                  ⚠️ {conflitti.length} conflitt{conflitti.length === 1 ? "o" : "i"} trovat{conflitti.length === 1 ? "o" : "i"}
                </div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", marginBottom: "10px", lineHeight: 1.5 }}>
                  Le seguenti celle hanno prenotazioni attive e verranno modificate dal nuovo layout.
                  Le prenotazioni rimarranno attive ma potrebbero risultare inconsistenti.
                </div>
                <div style={{ maxHeight: "120px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
                  {Object.entries(
                    conflitti.reduce((acc, c) => {
                      const key = `${c.cellaId}: ${c.tipoVecchio} → ${c.tipoNuovo ?? "rimossa"}`;
                      acc[key] = (acc[key] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([label, count]) => (
                    <div key={label} style={{
                      fontSize: "11.5px", color: "rgba(255,255,255,0.6)",
                      background: "rgba(255,255,255,0.05)", borderRadius: "6px",
                      padding: "4px 8px", display: "flex", justifyContent: "space-between",
                    }}>
                      <span>{label}</span>
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>{count} data{count > 1 ? "e" : ""}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: "11px", color: "rgba(239,68,68,0.7)", marginTop: "10px" }}>
                  Premi "Conferma e pubblica" per procedere comunque oppure "Annulla" per rivedere il layout.
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="sand" onClick={conflitti.length > 0 ? handlePublish : handleAnalizzaEPubblica} disabled={publishing || analizzando || !publishDal || !publishAl} fullWidth>
                {publishing || analizzando ? <Spinner size={14} /> : conflitti.length > 0 ? "Conferma e pubblica" : "Pubblica disponibilità"}
              </Button>
              <Button variant="ghost" onClick={() => { setShowPublish(false); setConflitti([]); }}>Annulla</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}